import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  FaRocket, 
  FaUsers, 
  FaHeart, 
  FaLeaf,
  FaHandshake,
  FaGlobe
} from 'react-icons/fa';

const About = () => {
  const values = [
    {
      icon: <FaHeart className="h-8 w-8" />,
      title: "Zero Platform Fees",
      description: "We take 0% fees because we're a community service focused on helping people connect."
    },
    {
      icon: <FaHandshake className="h-8 w-8" />,
      title: "Fair for Everyone",
      description: "Drivers keep 100% of earnings, riders set their own prices. Simple, fair, and transparent."
    },
    {
      icon: <FaUsers className="h-8 w-8" />,
      title: "Community First",
      description: "Built by iautenticai as a community service. Technology that serves people, not shareholders."
    },
    {
      icon: <FaGlobe className="h-8 w-8" />,
      title: "Open Platform",
      description: "No arbitrary restrictions, no sudden deactivations. An open platform that treats everyone fairly."
    }
  ];

  const team = [
    {
      name: "iautenticai Team",
      role: "Community Service Developers",
      bio: "Dedicated team building technology for people, not profit. Committed to keeping RideEase forever free.",
      image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=300&h=300&fit=crop"
    },
    {
      name: "The Community",
      role: "True Owners",
      bio: "RideEase belongs to the drivers and riders who use it. You are the real stakeholders, not shareholders.",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=300&h=300&fit=crop"
    },
    {
      name: "Every Driver",
      role: "100% Earnings",
      bio: "Every driver who keeps their full earnings through our zero-fee community service is part of our success story.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face"
    },
    {
      name: "Every Rider",
      role: "Fair Pricing",
      bio: "Every rider who sets their own fair prices and connects directly with drivers makes the community stronger.",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face"
    }
  ];

  const milestones = [
    {
      year: "2024",
      title: "Community Service Launch",
      description: "iautenticai company created Boulevard as a community service for fair ride sharing connections."
    },
    {
      year: "2024",
      title: "Zero-Fee Commitment",
      description: "Launched as a free community platform - 0% platform fees, built to serve the community."
    },
    {
      year: "2024",
      title: "Community Adoption",
      description: "Drivers and riders discover the benefits of fair pricing and direct connections."
    },
    {
      year: "2025",
      title: "Growing Community",
      description: "Expanding to serve more communities with simple, fair ride sharing connections."
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Boulevard - Community Service by iautenticai</title>
        <meta name="description" content="Boulevard is a community service by iautenticai company. 0% platform fees, fair pricing, simple ride sharing connections. Built to serve the community." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              About Boulevard - A Community Service
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built by iautenticai company as a community service. We believe 
              transportation should be fair, simple, and controlled by the 
              people who use it - riders and drivers working together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Our Mission: Community Service
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Boulevard is a simple community service for connecting riders and drivers. We charge zero fees 
                and let participants control their own pricing. Our platform is built by iautenticai company 
                as a community service - technology serving people through simple, fair ride sharing.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We believe drivers should keep 100% of their earnings and riders should pay fair prices 
                they set themselves. Simple, transparent ride-sharing that works for everyone in the community.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <FaGlobe className="text-primary-600" />
                  <span className="text-sm font-medium text-gray-700">Global Impact</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaUsers className="text-primary-600" />
                  <span className="text-sm font-medium text-gray-700">Community Driven</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop"
                alt="Team collaboration"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary-600 text-white p-6 rounded-xl">
                <div className="text-2xl font-bold">10K+</div>
                <div className="text-sm">Happy Users</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card p-6 text-center"
              >
                <div className="text-primary-600 mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones in our mission to transform transportation
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-primary-200"></div>
            
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex items-center mb-12 ${
                  index % 2 === 0 ? 'justify-start' : 'justify-end'
                }`}
              >
                <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8'}`}>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <div className="text-primary-600 font-bold text-lg mb-2">
                      {milestone.year}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600">
                      {milestone.description}
                    </p>
                  </div>
                </div>

                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full border-4 border-white shadow-lg"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-600">
              The passionate people behind RideEase
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card p-6 text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600 text-sm">
                  {member.bio}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              Join Our Mission
            </h2>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Be part of the transportation revolution. Whether you're a user, partner, 
              or investor, there's a place for you in our journey.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 justify-center">
                <a
                  href="https://t.me/Rideatease_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white hover:bg-blue-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                  Join as Rider
                </a>
                <a
                  href="https://t.me/Rideeasedriver_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white hover:bg-green-700 font-semibold py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                  Join as Driver
                </a>
              </div>
              <a
                href="/contact"
                className="btn-secondary text-white border-white hover:bg-white hover:text-primary-600"
              >
                Get In Touch
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;
